import * as store from '../state/store';
import { KEYBOARD_HEIGHT_MIN, currentKeyboardHeight, keyboardHeightMax } from './keyboard';

/**
 * A drag handle above the keyboard that sets a manual height (persisted). Works
 * the same whether the keyboard is drawn on one or two rows, since the height is
 * the total budget the layout divides between the rows.
 */
export function installKeyboardResize(handle: HTMLElement): void {
  const clampH = (h: number): number => Math.round(Math.max(KEYBOARD_HEIGHT_MIN, Math.min(keyboardHeightMax(), h)));

  let startY = 0;
  let startH = 0;
  let dragging = false;

  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    // The handle sits on top of the keyboard, so dragging up (dy < 0) grows it.
    store.update({ keyboardHeight: clampH(startH - (e.clientY - startY)) });
  };
  const onUp = (e: PointerEvent): void => {
    dragging = false;
    handle.classList.remove('dragging');
    handle.releasePointerCapture?.(e.pointerId);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
  };

  handle.addEventListener('pointerdown', (e) => {
    dragging = true;
    startY = e.clientY;
    startH = store.get('keyboardHeight') ?? currentKeyboardHeight();
    handle.classList.add('dragging');
    handle.setPointerCapture?.(e.pointerId);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    e.preventDefault();
  });

  handle.addEventListener('keydown', (e) => {
    const step = e.key === 'ArrowUp' ? 12 : e.key === 'ArrowDown' ? -12 : 0;
    if (step === 0) return;
    e.preventDefault();
    const cur = store.get('keyboardHeight') ?? currentKeyboardHeight();
    store.update({ keyboardHeight: clampH(cur + step) });
  });

  // Double-click resets to the automatic (viewport-based) height.
  handle.addEventListener('dblclick', () => store.update({ keyboardHeight: null }));
}
